
import React, { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Loader2, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { KolHierarchicalPriceListRow } from "./price-list/KolHierarchicalPriceListRow";
import { KolPriceListEmptyState } from "./price-list/KolPriceListEmptyState";
import { usePriceListData } from "./price-list/usePriceListData";
import { TablePagination } from "@/components/ui/table-pagination";
import { useAuth } from "@/contexts/AuthContext";
import { TopProgressBar } from "@/components/ui/top-progress-bar";

interface KolPriceListTableProps {
  isLoading?: boolean;
  onDataChange?: () => void;
  hasError?: boolean;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  hasFullAccess?: boolean;
  currentUserName?: string;
}

export function KolPriceListTable({ 
  isLoading: externalLoading = false,
  onDataChange,
  hasError: externalHasError = false,
  searchTerm: externalSearchTerm = "",
  onSearchChange: externalOnSearchChange,
  hasFullAccess = true,
  currentUserName = ''
}: KolPriceListTableProps) {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, currentRecord: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const { toast } = useToast();
  const { hasBuAccess } = useAuth();

  // Use internal search if no external search is provided
  const searchTerm = externalSearchTerm || internalSearchTerm;
  const handleSearchChange = externalOnSearchChange || setInternalSearchTerm;

  // Use the custom hook for data management
  const {
    data,
    totalCount,
    isLoading: dataLoading,
    hasError: dataHasError,
    refetch,
    updatePrice,
    updateCurrency
  } = usePriceListData({
    searchTerm,
    hasFullAccess,
    currentUserName,
    page: currentPage,
    pageSize
  });

  const isLoading = externalLoading || dataLoading;
  const hasError = externalHasError || dataHasError;

  // Optimistic update handler for prices
  const handlePriceUpdate = useCallback(async (kolDbId: string, contentType: string, newPrice: number) => {
    if (hasBuAccess) {
      toast({
        title: "Access Denied",
        description: "BU Access users cannot edit prices",
        variant: "destructive"
      });
      return;
    }
    
    const updateKey = `${kolDbId}-${contentType}`;
    setPendingUpdates(prev => new Set(prev).add(updateKey));
    
    try {
      await updatePrice(kolDbId, contentType, newPrice);
    } catch (error) {
      console.error("Update failed:", error);
      toast({
        title: "Error",
        description: "Update failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setPendingUpdates(prev => {
        const updated = new Set(prev);
        updated.delete(updateKey);
        return updated;
      });
    }
  }, [updatePrice, toast, hasBuAccess]);

  // Optimistic update handler for currency
  const handleCurrencyUpdate = useCallback(async (kolDbId: string, newCurrency: string) => {
    if (hasBuAccess) {
      toast({
        title: "Access Denied",
        description: "BU Access users cannot edit currency",
        variant: "destructive"
      });
      return;
    }
    
    const updateKey = `${kolDbId}-currency`;
    setPendingUpdates(prev => new Set(prev).add(updateKey));
    
    try {
      await updateCurrency(kolDbId, newCurrency);
    } catch (error) {
      console.error("Currency update failed:", error);
      toast({
        title: "Error",
        description: "Currency update failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setPendingUpdates(prev => {
        const updated = new Set(prev);
        updated.delete(updateKey);
        return updated;
      });
    }
  }, [updateCurrency, toast, hasBuAccess]);

  const isPending = useCallback((rowId: string, field?: string) => {
    if (field) {
      return pendingUpdates.has(`${rowId}-${field}`);
    }
    // Check if any field is pending for this row
    for (const key of Array.from(pendingUpdates)) {
      if (key.startsWith(`${rowId}-`)) {
        return true;
      }
    }
    return false;
  }, [pendingUpdates]);

  // Enhanced sync prices using kol_db.price_content_type as single source of truth
  const handleSyncPrices = async () => {
    if (hasBuAccess) {
      toast({
        title: "Access Denied",
        description: "BU Access users cannot sync prices",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSyncing(true);
      setSyncProgress({ current: 0, total: 0, currentRecord: 'Initializing...' });
      console.log("Starting enhanced price sync using price_content_type column...");
      
      let updatedCount = 0;
      let failedMatches: string[] = [];
      let processedRecords = 0;
      
      // Step 1: Fetch ALL kol_db records with price_content_type data (no pagination)
      console.log("Fetching all kol_db records with pricing data...");
      setSyncProgress({ current: 0, total: 0, currentRecord: 'Fetching pricing data...' });
      
      const { data: kolDbRecords, error: kolDbError } = await supabase
        .from('kol_db')
        .select('id, creator_name, price_content_type')
        .not('price_content_type', 'is', null)
        .neq('price_content_type', '{}');
      
      if (kolDbError) {
        throw new Error(`Failed to fetch kol_db records: ${kolDbError.message}`);
      }
      
      if (!kolDbRecords || kolDbRecords.length === 0) {
        toast({
          title: "No Pricing Data",
          description: "No kol_db records found with pricing information in price_content_type column.",
          variant: "destructive"
        });
        return;
      }
      
      console.log(`Found ${kolDbRecords.length} kol_db records with pricing data`);
      setSyncProgress({ current: 0, total: kolDbRecords.length, currentRecord: 'Starting sync...' });
      
      // Step 2: Process each kol_db record
      for (const kolRecord of kolDbRecords) {
        processedRecords++;
        const currentRecordInfo = `${kolRecord.creator_name} (${processedRecords}/${kolDbRecords.length})`;
        setSyncProgress({ current: processedRecords, total: kolDbRecords.length, currentRecord: currentRecordInfo });
        
        console.log(`Processing kol_db record ${processedRecords}/${kolDbRecords.length}: ${kolRecord.creator_name}`);
        
        // Parse the price_content_type JSONB
        const priceContentType = kolRecord.price_content_type;
        if (!priceContentType || typeof priceContentType !== 'object') {
          failedMatches.push(`${kolRecord.creator_name}: Invalid price_content_type format`);
          continue;
        }
        
        // Step 3: Find all kol_tracker records linked to this kol_db record
        const { data: trackerRecords, error: trackerError } = await supabase
          .from('kol_tracker')
          .select('id, name, type, expense, kol_db_id')
          .eq('kol_db_id', kolRecord.id);
        
        if (trackerError) {
          console.error(`Error fetching tracker records for ${kolRecord.creator_name}:`, trackerError);
          failedMatches.push(`${kolRecord.creator_name}: Error fetching tracker records`);
          continue;
        }
        
        if (!trackerRecords || trackerRecords.length === 0) {
          console.log(`No tracker records found for ${kolRecord.creator_name}`);
          continue;
        }
        
        console.log(`Found ${trackerRecords.length} tracker records for ${kolRecord.creator_name}`);
        
        // Step 4: Update tracker records based on matching content types
        for (const trackerRecord of trackerRecords) {
          if (!trackerRecord.type) {
            continue;
          }
          
          // Look for matching content type in price_content_type
          const matchingPrice = priceContentType[trackerRecord.type];
          
          if (matchingPrice && typeof matchingPrice === 'number' && matchingPrice > 0) {
            // Update the expense in kol_tracker
            const { error: updateError } = await supabase
              .from('kol_tracker')
              .update({ 
                expense: matchingPrice,
                updated_date: new Date().toISOString().split('T')[0]
              })
              .eq('id', trackerRecord.id);
            
            if (updateError) {
              console.error(`Error updating tracker record ${trackerRecord.id}:`, updateError);
              failedMatches.push(`${kolRecord.creator_name} - ${trackerRecord.type}: Update error`);
            } else {
              updatedCount++;
              console.log(`Updated ${trackerRecord.name} - ${trackerRecord.type}: $${matchingPrice}`);
            }
          } else {
            console.log(`No matching price found for ${trackerRecord.name} - ${trackerRecord.type}`);
          }
        }
      }
      
      // Step 5: Handle records without kol_db_id (fallback to name matching)
      setSyncProgress({ current: kolDbRecords.length, total: kolDbRecords.length, currentRecord: 'Processing orphan records...' });
      console.log("Processing records without kol_db_id using fallback name matching...");
      
      const { data: orphanRecords, error: orphanError } = await supabase
        .from('kol_tracker')
        .select('id, name, type, expense')
        .is('kol_db_id', null)
        .not('type', 'is', null);
      
      if (!orphanError && orphanRecords && orphanRecords.length > 0) {
        console.log(`Found ${orphanRecords.length} tracker records without kol_db_id`);
        
        for (const orphanRecord of orphanRecords) {
          // Try to find matching kol_db record by name
          const matchingKolDb = kolDbRecords.find(kol => 
            kol.creator_name.toLowerCase() === orphanRecord.name?.toLowerCase()
          );
          
          if (matchingKolDb && matchingKolDb.price_content_type) {
            const priceContentType = matchingKolDb.price_content_type;
            const matchingPrice = priceContentType[orphanRecord.type];
            
            if (matchingPrice && typeof matchingPrice === 'number' && matchingPrice > 0) {
              // Update both the expense and link the kol_db_id
              const { error: updateError } = await supabase
                .from('kol_tracker')
                .update({ 
                  expense: matchingPrice,
                  kol_db_id: matchingKolDb.id,
                  updated_date: new Date().toISOString().split('T')[0]
                })
                .eq('id', orphanRecord.id);
              
              if (updateError) {
                console.error(`Error updating orphan record ${orphanRecord.id}:`, updateError);
                failedMatches.push(`${orphanRecord.name} - ${orphanRecord.type}: Orphan update error`);
              } else {
                updatedCount++;
                console.log(`Updated orphan record ${orphanRecord.name} - ${orphanRecord.type}: $${matchingPrice} (linked to kol_db)`);
              }
            }
          }
        }
      }
      
      // Show results
      if (updatedCount > 0) {
        toast({
          title: "Sync Complete",
          description: `Successfully synced ${updatedCount} price records from kol_db.price_content_type${failedMatches.length > 0 ? `. ${failedMatches.length} failed matches.` : ''}`,
        });
        console.log(`Sync completed: ${updatedCount} records updated, ${failedMatches.length} failed`);
      } else {
        toast({
          title: "Sync Issues",
          description: `No records were updated. ${failedMatches.length} failed matches found.`,
          variant: "destructive"
        });
      }
      
      // Log failed matches for debugging
      if (failedMatches.length > 0) {
        console.warn("Failed matches:", failedMatches);
      }
      
      if (onDataChange) {
        onDataChange();
      }
      
    } catch (error) {
      console.error("Error syncing prices:", error);
      toast({
        title: "Sync Failed",
        description: "Failed to sync prices. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSyncing(false);
      setSyncProgress({ current: 0, total: 0, currentRecord: '' });
    }
  };

  // Handle page changes
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Calculate final processing state
  const finalIsProcessing = pendingUpdates.size > 0;

  // Calculate total pages based on actual rows (not aggregated data)
  const totalPages = Math.ceil(totalCount / pageSize);

  // Custom action bar
  const actionBarChildren = (
    <div className="flex items-center gap-3">
      {!hasBuAccess && (
        <Button 
          variant="outline" 
          onClick={handleSyncPrices}
          disabled={isSyncing || isLoading}
          className="h-8 text-foreground bg-background text-xs"
        >
          {isSyncing ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Syncing All Records...
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Sync All Prices
            </>
          )}
        </Button>
      )}

      {finalIsProcessing && (
        <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          Updating prices...
        </div>
      )}

      <div className="text-sm text-muted-foreground">
        {totalCount} price records
      </div>

      {!hasFullAccess && (
        <div className="text-xs text-muted-foreground italic">
          Limited access mode
        </div>
      )}

      {hasBuAccess && (
        <div className="text-sm text-red-600 font-medium bg-red-50 px-3 py-1 rounded-md">
          BU Access - Read Only Mode
        </div>
      )}
    </div>
  );

  // Show empty state if no data
  if (!isLoading && data.length === 0) {
    return (
      <div className="space-y-4 h-[calc(100vh-12rem)] flex flex-col">
        {/* Progress bar for sync operation */}
        <TopProgressBar
          isVisible={isSyncing}
          current={syncProgress.current}
          total={syncProgress.total}
          title="Syncing All Price Records"
          url={syncProgress.currentRecord}
        />
        
        <div className="flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            {actionBarChildren}
          </div>
          
          <div className="relative w-[250px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search creators, handlers, platforms..."
              className="pl-8 bg-background border-input text-foreground"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <KolPriceListEmptyState 
            hasFullAccess={hasFullAccess}
            isSearching={searchTerm.trim().length > 0}
            searchTerm={searchTerm}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-[calc(100vh-12rem)] flex flex-col">
      {/* Progress bar for sync operation */}
      <TopProgressBar
        isVisible={isSyncing}
        current={syncProgress.current}
        total={syncProgress.total}
        title="Syncing All Price Records"
        url={syncProgress.currentRecord}
      />
      
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          {actionBarChildren}
        </div>
        
        <div className="relative w-[250px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search creators, handlers, platforms..."
            className="pl-8 bg-background border-input text-foreground"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
      </div>

      <div className="border rounded-lg bg-card flex flex-col flex-1 min-h-0">
        <div className="flex-1 relative overflow-hidden">
          <div className="h-full overflow-y-auto">
            <table className="w-full border-collapse bg-background">
              <thead className="sticky top-0 z-10 bg-muted/50">
                <tr className="border-b">
                  <th className="px-4 py-3 text-left text-sm font-medium w-8"></th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Creator</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Content Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Price</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Currency</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">PICs</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading price data...
                      </div>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No price data available
                    </td>
                  </tr>
                ) : (
                  data.map((row) => (
                    <KolHierarchicalPriceListRow
                      key={row.id}
                      data={row}
                      onPriceUpdate={handlePriceUpdate}
                      onCurrencyUpdate={handleCurrencyUpdate}
                      isPending={isPending}
                      isOptimisticProcessing={finalIsProcessing}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex-shrink-0 border-t bg-card">
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              showPageSizeSelect={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default KolPriceListTable;
